import {
  Tag,
  Space,
  Button,
  Tooltip,
  Typography,
  Badge,
  message,
} from "antd";
import {
  StarFilled,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  TagsOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { STORES } from "../BlogsTable/stores";

const labelForHash = (hash) =>
  STORES.find((s) => s.hash === hash)?.label || "";

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString("en-GB");
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(String(text || ""));
    message.success("Copied");
  } catch {
    message.error("Copy failed");
  }
};

const buildColumns = ({
  onView,
  onEdit,
  onManageTags,
  onHardDelete,
  page = 1,
  limit = 20,
  tagCountMap = {},
}) => [
  {
    title: "Sr. No",
    key: "sno",
    width: 80,
    align: "center",
    render: (_, __, index) => (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {(page - 1) * limit + index + 1}
      </Typography.Text>
    ),
  },
    {
    title: "Store",
    key: "store",
    width: 150,
    sorter: (a, b) => {
      const la = a.storeLabel || labelForHash(a.storeHash) || "";
      const lb = b.storeLabel || labelForHash(b.storeHash) || "";
      return la.localeCompare(lb);
    },
    render: (_, record) => {
      const lbl = record.storeLabel || labelForHash(record.storeHash) || "";
      return (
        <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
          {lbl ? (
            <Tag color="geekblue" style={{ marginRight: 0 }}>
              {lbl}
            </Tag>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              —
            </Typography.Text>
          )}
          <Tooltip title={record.storeHash || ""}>
            <Typography.Text code style={{ fontSize: 11 }}>
              {record.storeHash || "—"}
            </Typography.Text>
          </Tooltip>
        </Space>
      );
    },
  },
  {
    title: "Bambuser Video ID",
    dataIndex: "bamvideoId",
    key: "bamvideoId",
    width: 400,
    sorter: (a, b) => String(a.bamvideoId || "").localeCompare(String(b.bamvideoId || "")),
    render: (val) => (
      <Space size={4}>
        <Typography.Text code style={{ fontSize: 12 }}>
          {val}
        </Typography.Text>
        <Tooltip title="Copy">
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              copy(val);
            }}
          />
        </Tooltip>
      </Space>
    ),
  },
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    ellipsis: true,
    sorter: (a, b) => String(a.title || "").localeCompare(String(b.title || "")),
    render: (val, record) => (
      <Space size={6}>
        {val ? (
          <Typography.Text>{val}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )}
        {Number(record.isDefault) === 1 && (
          <Tag color="gold" icon={<StarFilled />} style={{ marginRight: 0 }}>
            Default
          </Tag>
        )}
      </Space>
    ),
  },
  {
    title: "Actions",
    key: "actions",
    width: 250,
    fixed: "right",
    align: "center",
    render: (_, record) => {
      const tagCount = tagCountMap[String(record.id)] ?? record.keyword_points?.length ?? 0;
      return (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Manage keyword tags">
            <Badge count={tagCount} size="small" offset={[-2, 2]}>
              <Button
                size="small"
                icon={<TagsOutlined />}
                onClick={() => onManageTags?.(record)}
              >
                Tags
              </Button>
            </Badge>
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit?.(record)}
            />
          </Tooltip>
          <Tooltip title="View details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onView?.(record)}
            />
          </Tooltip>
          <Tooltip title="Delete permanently">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onHardDelete?.(record)}
            />
          </Tooltip>
        </Space>
      );
    },
  },
];

export default buildColumns;